import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import RefreshButton from 'components/RefreshButton';
import { Box, Fade, Grid, Stack, Typography } from '@mui/material';
import CustomSnackbar from 'components/CustomSnackbar';
import OcpDialogSelect from './OcpDialogSelect';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  clearSensorData,
  setOcpAvgsDatesObj,
  setSelectedDevicesIds,
  fetchOcpAvgs,
  fetchUserDevices,
  setCurrentPage,
} from 'stores/sensor/sensorStore';
import Charts from './Charts.js';
import { getSelectedTimeRangeForNavDisplay } from 'helpers/helpers.js';
import { filterDevices, getSensorOptions } from '../../helpers/filterHelpers';
import EmptyStateMessage from '../../components/MessageBox/EmptyStateMessage';

function OccupancyAverages() {
  // --- Local UI state ---
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [localSelectedSensors, setLocalSelectedSensors] = useState([]); // array of ids only
  const [lastSearchedSensorsKey, setLastSearchedSensorsKey] = useState('');
  const [isRefreshGlowing, setIsRefreshGlowing] = useState(false);
  const didInitialFetchRef = useRef(false);
  const hasInitializedSensorSelectionRef = useRef(false);
  const lastSensorSelectionFiltersKeyRef = useRef(null);
  // avoid concurrent refreshes
  const isRefreshingRef = useRef(false);

  const dispatch = useDispatch();

  // --- Redux state ---
  const { selectedFilterObject } = useSelector(s => s.persistence);
  const { distributor, customer, building, floor, area, hvacEquipment, status } = selectedFilterObject || {};
  const searchString = useSelector(s => s.persistence.searchString || '');

  const ocpDatesObj = useSelector(s => s.sensors.ocpDatesObj, shallowEqual);
  const currentUser = useSelector(s => s.sensors.currentUser);
  const devices = useSelector(s => s.sensors.devices || []);

  // store expects array of IDs — keep it consistent
  // const selectedDevicesIds = useSelector(s => s.sensors.selectedDevicesIds || [], shallowEqual);
  const ocpAvgsData = (useSelector(s => s.sensors.data?.ocpAvgsData) || []).filter(d => d?.data != null);
  const showSnackbar = useCallback(message => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);
  const handleSnackbarClose = () => setSnackbarOpen(false);

  // --- Derived lists ---
  const filteredDevices = useMemo(() => {
    if (devices.length === 0) return [];
    return filterDevices(
      devices,
      {
        distributor,
        customer,
        building,
        floor,
        area,
        hvacEquipment,
        status,
        searchString,
      },
      currentUser,
    );
  }, [devices, distributor, customer, building, floor, area, hvacEquipment, status, searchString]);

  const sensorOptions = useMemo(() => getSensorOptions(filteredDevices), [filteredDevices]);
  // cache a stable, sorted list of available IDs for effect deps
  const availableSensorIds = useMemo(
    () =>
      (filteredDevices || [])
        .map(d => d.id)
        .filter(Boolean)
        .sort(),
    [filteredDevices],
  );
  const availableSensorIdsKey = useMemo(() => availableSensorIds.join(','), [availableSensorIds]);
  const sensorSelectionFiltersKey = useMemo(
    () => [distributor, customer, building, floor, area, hvacEquipment, status, searchString].join('|'),
    [distributor, customer, building, floor, area, hvacEquipment, status, searchString],
  );
  const localSelectedSensorsKey = useMemo(
    () => [...localSelectedSensors].filter(Boolean).sort().join(','),
    [localSelectedSensors],
  );

  // Select every available sensor on first load and whenever a non-device
  // filter changes. Otherwise retain the user's partial selection.
  useEffect(() => {
    if (availableSensorIds.length > 0) {
      const availableIds = new Set(availableSensorIds);
      const filtersChanged = lastSensorSelectionFiltersKeyRef.current !== sensorSelectionFiltersKey;
      lastSensorSelectionFiltersKeyRef.current = sensorSelectionFiltersKey;

      setLocalSelectedSensors(currentSelection => {
        if (!hasInitializedSensorSelectionRef.current || filtersChanged) {
          hasInitializedSensorSelectionRef.current = true;
          return availableSensorIds;
        }

        return currentSelection.filter(id => availableIds.has(id));
      });
    } else {
      hasInitializedSensorSelectionRef.current = false;
      setLocalSelectedSensors([]);
    }
  }, [availableSensorIdsKey, sensorSelectionFiltersKey]);

  useEffect(() => {
    if (lastSearchedSensorsKey === '' && localSelectedSensors.length > 0) {
      setLastSearchedSensorsKey(localSelectedSensorsKey);
    }
  }, [lastSearchedSensorsKey, localSelectedSensors.length, localSelectedSensorsKey]);

  // selection handlers
  const handleSensorChange = useCallback(selectedSensorIds => {
    setLocalSelectedSensors(selectedSensorIds);
  }, []);

  const showRefreshGlow = useCallback(() => {
    setIsRefreshGlowing(true);
  }, []);

  // --- API ---
  const callOccupancyAPI = useCallback(
    async (sensorIds, { buildingId, floorNumber, areaName }) => {
      if (!Array.isArray(sensorIds) || sensorIds.length === 0) return;
      if (isRefreshingRef.current) return;
      isRefreshingRef.current = true;
      try {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        await dispatch(
          fetchOcpAvgs({
            ocpDatesObj,
            building_id: buildingId,
            floor_number: floorNumber !== 'all' ? floorNumber : undefined,
            area_name: areaName !== 'all' ? areaName : undefined,
            device_ids: sensorIds,
            userTimeZone,
          }),
        );
      } catch (err) {
        console.error('❌ Error fetching occupancy averages:', err);
        showSnackbar('Error loading occupancy data. Please try again.');
      } finally {
        isRefreshingRef.current = false;
      }
    },
    [dispatch, ocpDatesObj, showSnackbar],
  );

  const refreshContent = useCallback(
    async (sensorsToUse, showErrors = false, { buildingId, floorNumber, areaName } = {}) => {
      if (!buildingId || buildingId === 'all') {
        if (showErrors) showSnackbar('Please select a building to view occupancy averages.');
        return;
      }

      // const effectiveIds =
      //   Array.isArray(sensorsToUse) && sensorsToUse.length > 0
      //     ? sensorsToUse
      //     : selectedDevicesIds.length > 0
      //       ? selectedDevicesIds
      //       : localSelectedSensors;

      const effectiveIds = Array.isArray(sensorsToUse) && sensorsToUse.length > 0 ? sensorsToUse : [];

      if (!effectiveIds || effectiveIds.length === 0) {
        if (showErrors) showSnackbar('No sensors available for the selected filters.');
        return;
      }

      // keep store in sync with ids only
      dispatch(setSelectedDevicesIds(effectiveIds));
      await callOccupancyAPI(effectiveIds, { buildingId, floorNumber, areaName });
    },
    [callOccupancyAPI, dispatch, showSnackbar],
  );

  // --- Lifecycle ---
  // page marker
  useEffect(() => {
    dispatch(setCurrentPage('occupancy-averages'));
  }, [dispatch]);

  // fetch sensors list once
  useEffect(() => {
    dispatch(
      fetchUserDevices({
        requestedParams: ['id', 'name', 'dist_ids', 'cust_ids', 'building_id', 'floor_number', 'area_id', 'status', 'hvac_equipment_ids'],
      }),
    );
  }, [dispatch]);

  // run ASAP on mount (before paint) to kick the first fetch if possible
  useLayoutEffect(() => {
    if (didInitialFetchRef.current) return;
    if (!building || building === 'all') return;
    if (availableSensorIds.length === 0) return;
    didInitialFetchRef.current = true;
    dispatch(setSelectedDevicesIds(availableSensorIds));
    callOccupancyAPI(availableSensorIds, { buildingId: building, floorNumber: floor, areaName: area });
  }, [building, floor, area, availableSensorIds, callOccupancyAPI, dispatch]);

  // react to changes: distributor, customer, building, floor, area, ocpDatesObj
  // NOTE: filteredDevices changes no longer trigger fetch, only manual search
  useEffect(() => {
    if (!building || building === 'all') return;
    if (localSelectedSensors.length === 0) return;
    refreshContent(localSelectedSensors, false, { buildingId: building, floorNumber: floor, areaName: area });
  }, [refreshContent]);

  // cleanup only on unmount
  useEffect(() => {
    return () => {
      dispatch(clearSensorData());
      dispatch(setSelectedDevicesIds([]));
    };
  }, [dispatch]);

  // manual actions
  const handleManualRefresh = selectedSensorsList => {
    refreshContent(selectedSensorsList, true, { buildingId: building, floorNumber: floor, areaName: area });
  };

  const handleSearchClick = useCallback(() => {
    if (localSelectedSensors.length === 0) {
      showSnackbar('Please select at least one sensor.');
      return;
    }
    if (building === 'all') {
      showSnackbar('Please select a building.');
      return;
    }
    setLastSearchedSensorsKey(localSelectedSensorsKey);
    refreshContent(localSelectedSensors, false, { buildingId: building, floorNumber: floor, areaName: area });
  }, [localSelectedSensors, localSelectedSensorsKey, refreshContent, showSnackbar, building, floor, area]);

  const showCharts = ocpAvgsData.length > 0;
  const emptyStateMessage =
    building === 'all'
      ? 'Please select a building to view occupancy rates'
      : availableSensorIds.length === 0
        ? 'No sensors found with current filters'
        : 'No occupancy data available for selected sensors and time range';

  return (
    <DashboardLayout>
      <DashboardNavbar objectName={'Occupancy averages'} />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>

      <CustomSnackbar
        open={snackbarOpen}
        duration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        vertical="top"
        horizontal="center"
      />

      <MDBox pt={{ xs: 7, sm: 1 }} pb={1} className="ocp-avgs-container">
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MDBox
              mb={1}
              px={2}
              variant="gradient"
              borderRadius="lg"
              coloredShadow="light"
              id="topBar"
              sx={{
                background: '#DEE4F9',
                position: 'relative',
                zIndex: 1200,
              }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                justifyContent="space-between"
                spacing={{ xs: 1, sm: 0 }}>
                <Stack direction="row" ml={2} spacing={2} alignItems="end" className="top-bar">
                  <MDTypography variant="h4" color="dark">
                    Occupancy rates
                  </MDTypography>
                </Stack>

                <Stack sx={{ display: 'flex', flexDirection: 'row', width: { xs: '100%', sm: 'auto' } }}>
                  <DevicesListFilter
                    status={false}
                    search={false}
                    hvacEquipment
                    sensorsSelect={true}
                    selectedSensors={localSelectedSensors}
                    onSensorChange={handleSensorChange}
                    onSelectionChange={showRefreshGlow}
                    sensorOptions={sensorOptions}
                    groupSensorsByFloor
                    showSearchButton={false}
                    onSearchClick={handleSearchClick}
                    searchButtonDisabled={localSelectedSensorsKey === lastSearchedSensorsKey}
                  />
                </Stack>

                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <OcpDialogSelect
                    buttonTitle="Time Range"
                    buttonSelectedTimeRangeForDisplay={getSelectedTimeRangeForNavDisplay(
                      ocpDatesObj?.startDate,
                      ocpDatesObj?.endDate,
                    )}
                    selectedStartDate={ocpDatesObj?.startDate}
                    selectedEndDate={ocpDatesObj?.endDate}
                    dialogTitle="Choose Time Range"
                    color="black"
                    selectLabel="Time Range"
                    default={ocpDatesObj}
                    onSelectionOK={v => dispatch(setOcpAvgsDatesObj(v))}
                    onClose={() =>
                      refreshContent(localSelectedSensors, false, {
                        buildingId: building,
                        floorNumber: floor,
                        areaName: area,
                      })
                    }
                    toolTip="You can select up to seven days range."
                  />

                  <RefreshButton
                    isGlowing={isRefreshGlowing}
                    onClick={() => {
                      setIsRefreshGlowing(false);
                      handleManualRefresh(localSelectedSensors);
                    }}
                  />
                </Stack>
              </Stack>
            </MDBox>
          </Grid>
        </Grid>

        {showCharts ? (
          <Charts data={ocpAvgsData} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: building === 'all' ? '3rem' : 0,
              pt: building === 'all' ? 0 : '20rem',
            }}>
            {building === 'all' ? (
              <EmptyStateMessage message={emptyStateMessage} maxHeight="10rem" width="30rem" />
            ) : (
              <Fade in timeout={800} style={{ transitionDelay: '1000ms' }}>
                <Typography sx={{ fontSize: '1.6rem', fontWeight: 500 }}>{emptyStateMessage}</Typography>
              </Fade>
            )}
          </Box>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default OccupancyAverages;
